/* 金鑰派生與環境檢查。
   WebCrypto 沒有 Argon2id（W3C 規範全文 0 次提及），所以用 hash-wasm。
   實測：m=64MiB t=3 在桌機 150ms，純 JS 實作要 707ms —— 差 5 倍。 */
import { argon2id } from "hash-wasm";

export interface Argon2Params {
  name: "argon2id";
  /** 記憶體成本，單位 KiB。65536 = 64MiB */
  m: number;
  /** 迭代次數 */
  t: number;
  /** 平行度 */
  p: number;
  saltB64: string;
}

/** OWASP 建議的最低值是 m=19MiB t=2；這裡刻意高一級。 */
export const DEFAULT_ARGON2 = { name: "argon2id", m: 65536, t: 3, p: 1 } as const;

export function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

export function toB64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function fromB64(text: string): Uint8Array {
  const bin = atob(text);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function utf8(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

export function normalizePassphrase(passphrase: string): string {
  return passphrase.normalize("NFKC");
}

export async function deriveRawKey(passphrase: string, params: Argon2Params): Promise<Uint8Array> {
  return argon2id({
    password: utf8(normalizePassphrase(passphrase)),
    salt: fromB64(params.saltB64),
    parallelism: params.p,
    iterations: params.t,
    memorySize: params.m,
    hashLength: 32,
    outputType: "binary",
  });
}

export interface SecureContextReport {
  secure: boolean;
  subtle: boolean;
  isSecureContext: boolean | undefined;
  protocol: string;
  host: string;
  reason: string;
}

/** crypto.subtle 只在安全上下文存在。沒有它，整層加密會「靜默失效」——
   所以這件事必須是一個能回報原因的檢查，而不是一句假設。 */
export function checkSecureContext(g: Record<string, unknown> = globalThis as unknown as Record<string, unknown>): SecureContextReport {
  const win = g as { crypto?: { subtle?: unknown }; isSecureContext?: boolean; location?: { protocol?: string; hostname?: string } };
  const subtle = !!win.crypto?.subtle;
  const isSecureContext = typeof win.isSecureContext === "boolean" ? win.isSecureContext : undefined;
  const protocol = win.location?.protocol ?? "(node)";
  const host = win.location?.hostname ?? "(node)";
  let reason = "";
  if (!subtle) {
    reason = "這個環境沒有 crypto.subtle，加密無法運作。通常是因為不是安全上下文：請用 https:// 或 localhost 開啟。";
  } else if (isSecureContext === false) {
    reason = "不是安全上下文（" + protocol + "//" + host + "），加密可能無法運作。請改用 https:// 或 localhost。";
  }
  return { secure: subtle && isSecureContext !== false, subtle, isSecureContext, protocol, host, reason };
}
